import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { AUTOMATION_QUERY_KEYS } from '../../queryKeys';

interface MutationVariables {
	id: string;
	cancelOnReply: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Hook for setting the cancelOnReply property of an automation template.
 *
 * This mutation enables/disables automatic cancellation of pending automation steps
 * when a contact replies to any communication. When enabled, a reply indicates active
 * engagement and prevents further automated messages from being sent.
 *
 * @param options - Configuration options for the mutation
 * @param options.onSuccess - Callback fired when the mutation succeeds
 * @param options.onError - Callback fired when the mutation fails
 * @returns A React Query mutation object
 */
export function useAutomationTemplateSetCancelOnReplyMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IAutomationTemplate, variables: MutationVariables) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, cancelOnReply, impersonationContext }: MutationVariables) => {
			return userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(id)}/CancelOnReply`,
					impersonationContext,
					queryParams: {
						cancelOnReply,
					},
				}),
				'PUT'
			);
		},
		onError,
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(variables.id) });
			onSuccess?.(data, variables);
		},
	});
}
